/**
 * CDAUR2Service - Cliente para Worker CDAU R2
 *
 * Gestiona la carga lazy de datos CDAU (Callejero Digital de Andalucía Unificado)
 * desde el Worker R2 con caché en memoria y búsqueda fuzzy.
 *
 * Datos: 2.695.764 portales en 785 municipios
 * CRS: EPSG:25830 (UTM30 ETRS89)
 *
 * @module services/cdau
 * @version 1.0.0
 */

import uFuzzy from '@leeoniya/ufuzzy';
import type {
  CDAUMunicipio,
  CDAUPortal,
  CDAUSearchOptions,
  CDAUSearchResult,
} from '../../types/cdauR2';
import { PROVINCIAS_ANDALUCIA } from '../../types/catastro';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';
import { crearUFuzzyConGuarda, type UFuzzyConGuarda } from '@/lib/fuzzySearch';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE`).
 */
const WORKER_BASE_URL = WORKER_BASE;

// RUTA CANÓNICA CDAU: cdau/cdau-{ine5}.json.gz  → 785/785 ✅ (verificado 2026-03-22)
// ⚠️  NO usar: cdau/{ine5}.json.gz (sin prefijo "cdau-")
//     Esa ruta tiene gaps conocidos: INE 29064 (Jubrique) e INE 41091 (Sevilla)
//     devuelven {error} en vez de datos. Causa confusión en inspección manual.

const CACHE_MAX_ITEMS = 20;
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutos

/**
 * Artículos y preposiciones que generan ruido en matching de direcciones.
 * Se eliminan tanto del query como del índice para evitar penalización
 * en calculateScore (ej: "AVENIDA DE LA CONSTITUCION" vs "AVENIDA CONSTITUCION").
 * Orden: multi-palabra primero, luego mono-palabra.
 */
const ADDRESS_NOISE_WORDS = /\b(?:DE\s+LAS|DE\s+LOS|DE\s+LA|DEL|DE|LAS|LOS|LA|EL|AL)\b/g;

/** Configuración uFuzzy optimizada para direcciones españolas */
const UFUZZY_OPTIONS: uFuzzy.Options = {
  intraMode: 1,
  intraIns: 1,
  intraSub: 1,
  intraTrn: 1,
  intraDel: 1,
  interIns: 6,
  interLft: 0,
  interRgt: 0,
};

// =============================================================================
// TIPOS INTERNOS
// =============================================================================

interface CacheEntry {
  data: CDAUMunicipio;
  addressIndex: string[];
  portalMap: Map<string, CDAUPortal>;
  timestamp: number;
}

// =============================================================================
// SINGLETON SERVICE
// =============================================================================

let serviceInstance: CDAUR2Service | null = null;

export function getCDAUR2Service(): CDAUR2Service {
  if (!serviceInstance) {
    serviceInstance = new CDAUR2Service();
  }
  return serviceInstance;
}

/** Reset singleton (útil para tests) */
export function resetCDAUR2Service(): void {
  if (serviceInstance) {
    serviceInstance.clearCache();
  }
  serviceInstance = null;
}

// =============================================================================
// CLASE PRINCIPAL
// =============================================================================

export class CDAUR2Service {
  private readonly baseUrl: string;
  private readonly cache: Map<string, CacheEntry> = new Map();
  private readonly maxCacheSize: number;
  private readonly cacheTTL: number;
  private readonly ufuzzy: UFuzzyConGuarda;
  private loadingPromises: Map<string, Promise<CDAUMunicipio | null>> = new Map();

  constructor(baseUrl?: string, maxCacheSize = CACHE_MAX_ITEMS, cacheTTL = CACHE_TTL_MS) {
    this.baseUrl = baseUrl || WORKER_BASE_URL;
    this.maxCacheSize = maxCacheSize;
    this.cacheTTL = cacheTTL;
    this.ufuzzy = crearUFuzzyConGuarda(UFUZZY_OPTIONS);
  }

  /** Carga datos CDAU de un municipio */
  async loadMunicipio(codigoINE: string): Promise<CDAUMunicipio | null> {
    if (!/^\d{5}$/.test(codigoINE)) {
      console.warn(`[CDAUR2] Código INE inválido: ${codigoINE}`);
      return null;
    }

    const provincia = codigoINE.substring(0, 2);
    if (!PROVINCIAS_ANDALUCIA.includes(provincia as (typeof PROVINCIAS_ANDALUCIA)[number])) {
      console.warn(`[CDAUR2] Provincia no andaluza: ${provincia}`);
      return null;
    }

    const cached = this.getFromCache(codigoINE);
    if (cached) return cached.data;

    const existingPromise = this.loadingPromises.get(codigoINE);
    if (existingPromise) return existingPromise;

    const loadPromise = this.fetchMunicipio(codigoINE);
    this.loadingPromises.set(codigoINE, loadPromise);

    try {
      const data = await loadPromise;
      if (data) {
        this.setInCache(codigoINE, data);
      }
      return data;
    } finally {
      this.loadingPromises.delete(codigoINE);
    }
  }

  /** Busca direcciones en un municipio con fuzzy matching */
  async search(options: CDAUSearchOptions): Promise<CDAUSearchResult[]> {
    const {
      codigoINE,
      direccion,
      numero,
      codigoPostal,
      maxResults = 5,
      threshold = 0.6,
    } = options;

    // Cargar municipio si no está en caché
    const municipio = await this.loadMunicipio(codigoINE);
    if (!municipio || municipio.portales.length === 0) {
      return [];
    }

    const cached = this.getFromCache(codigoINE);
    if (!cached) return [];

    // Normalizar query
    let normalizedQuery = this.normalizeAddress(direccion);
    if (normalizedQuery.length < 2) return [];

    // P3.19b: Detectar "s/n" (sin número) → mapear a numero:0
    // Tras normalización, "s/n" → "S N", "sn" → "SN"
    let effectiveNumero = numero;
    const snPattern = /\bS\s*N\b/;
    if (snPattern.test(normalizedQuery)) {
      normalizedQuery = normalizedQuery.replace(snPattern, '').replace(/\s+/g, ' ').trim();
      if (effectiveNumero === undefined) {
        effectiveNumero = 0;
      }
    }

    // P3.19d: Strip "KM X" (kilómetro) de búsquedas de carreteras
    // "CARRETERA GRANADA KM 3" → "CARRETERA GRANADA", evita polución en uFuzzy
    const kmPattern = /\bKM\s*\d+\b/g;
    if (kmPattern.test(normalizedQuery)) {
      normalizedQuery = normalizedQuery.replace(kmPattern, '').replace(/\s+/g, ' ').trim();
    }

    // Búsqueda fuzzy
    const [idxs, , order] = this.ufuzzy.search(cached.addressIndex, normalizedQuery);

    if (!idxs || idxs.length === 0) {
      return this.fallbackSearch(cached, normalizedQuery, effectiveNumero, codigoPostal, maxResults, threshold);
    }

    // Procesar resultados
    const results: CDAUSearchResult[] = [];
    const sortedIdxs = order ? order.map(i => idxs[i]) : idxs;

    for (const idx of sortedIdxs) {
      if (results.length >= maxResults * 2) break;

      const matchedAddress = cached.addressIndex[idx];
      const portal = cached.portalMap.get(matchedAddress);
      if (!portal) continue;

      // Filtrar por número si se especifica
      if (effectiveNumero !== undefined && portal.n !== effectiveNumero) continue;

      // Filtrar por código postal si se especifica
      if (codigoPostal && portal.cp !== codigoPostal) continue;

      const score = this.calculateScore(normalizedQuery, matchedAddress);
      if (score < threshold) continue;

      results.push({ portal, score, matchedAddress });
    }

    // Ordenar por score y aplicar bonus por número exacto
    results.sort((a, b) => {
      const aBonus = numero !== undefined && a.portal.n === numero ? 0.1 : 0;
      const bBonus = numero !== undefined && b.portal.n === numero ? 0.1 : 0;
      return (b.score + bBonus) - (a.score + aBonus);
    });

    return results.slice(0, maxResults);
  }

  // ===========================================================================
  // MÉTODOS PRIVADOS
  // ===========================================================================

  private async fetchMunicipio(codigoINE: string): Promise<CDAUMunicipio | null> {
    const url = `${this.baseUrl}/cdau/cdau-${codigoINE}.json.gz`;

    try {
      const response = await fetch(url);

      if (response.status === 404) {
        // L1.01b: Fallback WFS para municipios sin datos R2 (≈3 %)
        const wfsData = await this.tryWFSCacheFallback(codigoINE);
        if (wfsData) return wfsData;
        console.warn(`[CDAUR2] Municipio sin datos CDAU ni WFS: ${codigoINE}`);
        return null;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      // Parsear JSON: intentar directo primero, gzip si falla
      // response.clone() para preservar body en caso de fallback a gzip
      let rawData: Record<string, unknown>;
      try {
        const cloneable = typeof response.clone === 'function';
        const jsonResponse = cloneable ? response.clone() : response;
        rawData = await jsonResponse.json();
      } catch {
        // R2 sirve .json.gz sin Content-Encoding → bytes gzip crudos
        if (response.body && typeof DecompressionStream !== 'undefined') {
          const decompressed = response.body.pipeThrough(new DecompressionStream('gzip'));
          const text = await new Response(decompressed).text();
          rawData = JSON.parse(text);
        } else {
          throw new Error('No se pudo descomprimir respuesta gzip');
        }
      }

      // Adaptar schema R2 → tipos internos CDAUMunicipio
      const portalesRaw = (rawData.portales as Array<Record<string, unknown>>) || [];
      const data: CDAUMunicipio = {
        ine: (rawData.ine_mun as string) || (rawData.ine as string) || codigoINE,
        count: (rawData.count as number) || portalesRaw.length,
        portales: portalesRaw.map((p) => ({
          t: (p.t as string) || '',
          v: (p.v as string) || '',
          n: (p.n as number) || 0,
          cp: (p.cp as string) || '',
          x: p.x as number,
          y: p.y as number,
        })),
      };

      console.log(`[CDAUR2] Cargado ${codigoINE}: ${data.count} portales`);
      return data;
    } catch (error) {
      console.error(`[CDAUR2] Error cargando ${codigoINE}:`, error);
      return null;
    }
  }

  private getFromCache(codigoINE: string): CacheEntry | null {
    const entry = this.cache.get(codigoINE);
    if (!entry) return null;

    if (Date.now() - entry.timestamp > this.cacheTTL) {
      this.cache.delete(codigoINE);
      return null;
    }

    return entry;
  }

  private setInCache(codigoINE: string, data: CDAUMunicipio): void {
    // Evict LRU si es necesario
    if (this.cache.size >= this.maxCacheSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    // Construir índices
    const addressIndex: string[] = [];
    const portalMap = new Map<string, CDAUPortal>();

    for (const portal of data.portales) {
      const address = this.buildAddressString(portal);
      addressIndex.push(address);
      portalMap.set(address, portal);
    }

    this.cache.set(codigoINE, {
      data,
      addressIndex,
      portalMap,
      timestamp: Date.now(),
    });
  }

  /** Construye string de dirección normalizado para indexación */
  private buildAddressString(portal: CDAUPortal): string {
    // Formato: "TIPO VIA NUMERO" o "VIA NUMERO" si tipo vacío
    const parts = [portal.t, portal.v, String(portal.n)].filter(Boolean);
    return this.normalizeAddress(parts.join(' '));
  }

  /** Normaliza dirección para búsqueda */
  private normalizeAddress(address: string): string {
    return address
      .toUpperCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Eliminar diacríticos
      .replace(/[^\w\s]/g, ' ')        // Puntuación → espacios
      .replace(ADDRESS_NOISE_WORDS, '') // Eliminar artículos/preposiciones
      .replace(/\s+/g, ' ')            // Colapsar espacios
      .trim();
  }

  /** Calcula score de similitud entre query y candidato */
  private calculateScore(query: string, candidate: string): number {
    if (query === candidate) return 1.0;

    const queryWords = query.split(' ').filter(w => w.length > 0);
    const candidateWords = candidate.split(' ').filter(w => w.length > 0);

    if (queryWords.length === 0) return 0;

    let matchedWords = 0;
    let partialMatches = 0;

    for (const qWord of queryWords) {
      if (candidateWords.includes(qWord)) {
        matchedWords++;
      } else {
        const hasPartial = candidateWords.some(
          cWord => cWord.startsWith(qWord) || qWord.startsWith(cWord)
        );
        if (hasPartial) partialMatches++;
      }
    }

    const exactRecall = matchedWords / queryWords.length;
    const partialRecall = (partialMatches / queryWords.length) * 0.7;

    return Math.min(1.0, exactRecall + partialRecall);
  }

  /** Búsqueda fallback cuando uFuzzy no encuentra resultados */
  private fallbackSearch(
    cached: CacheEntry,
    query: string,
    numero: number | undefined,
    codigoPostal: string | undefined,
    maxResults: number,
    threshold: number
  ): CDAUSearchResult[] {
    const results: CDAUSearchResult[] = [];

    for (let i = 0; i < cached.addressIndex.length && results.length < maxResults * 3; i++) {
      const address = cached.addressIndex[i];
      const portal = cached.portalMap.get(address);
      if (!portal) continue;

      if (numero !== undefined && portal.n !== numero) continue;
      if (codigoPostal && portal.cp !== codigoPostal) continue;

      const score = this.calculateScore(query, address);
      if (score >= threshold) {
        results.push({ portal, score, matchedAddress: address });
      }
    }

    results.sort((a, b) => b.score - a.score);
    return results.slice(0, maxResults);
  }

  /** Limpia la caché */
  clearCache(): void {
    this.cache.clear();
  }

  /** Estadísticas de caché */
  getCacheStats(): { size: number; maxSize: number; municipios: string[] } {
    return {
      size: this.cache.size,
      maxSize: this.maxCacheSize,
      municipios: Array.from(this.cache.keys()),
    };
  }

  /** Verifica si un municipio está en caché */
  hasMunicipio(codigoINE: string): boolean {
    return this.getFromCache(codigoINE) !== null;
  }

  /** Obtiene número de portales en caché para un municipio */
  getPortalCount(codigoINE: string): number {
    const cached = this.getFromCache(codigoINE);
    return cached?.data.count ?? 0;
  }

  // ===========================================================================
  // L1.01b — Fallback WFS para municipios sin datos R2
  // ===========================================================================

  /**
   * Intenta obtener datos desde el caché WFS (IndexedDB → WFS online).
   * Solo se invoca cuando R2 devuelve 404 (≈3 % de municipios).
   * Adapta CDAUMunicipalityResponse → CDAUMunicipio (t='', cp='' admisibles).
   */
  private async tryWFSCacheFallback(codigoINE: string): Promise<CDAUMunicipio | null> {
    try {
      const { getCDAUWFSCacheService } = await import('./CDAUWFSCacheService');
      const resp = await getCDAUWFSCacheService().getPortales(codigoINE);
      if (!resp || resp.portales.length === 0) return null;

      const data: CDAUMunicipio = {
        ine: resp.ine_mun,
        count: resp.count,
        portales: resp.portales.map(p => ({
          t: '',   // WFS compacto no incluye tipoVia
          v: p.v,
          n: p.n,
          cp: '',  // WFS compacto no incluye CP
          x: p.x,
          y: p.y,
        })),
      };
      console.log(`[CDAUR2] WFS fallback para ${codigoINE}: ${data.count} portales`);
      return data;
    } catch (err) {
      console.warn(`[CDAUR2] WFS fallback falló para ${codigoINE}:`, err);
      return null;
    }
  }
}
